import React, { useEffect } from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { getBlogBySlug, getBlogsByTool } from '../data/blogPosts';
import { Calendar, Tag, ArrowLeft, ArrowRight } from 'lucide-react';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const post = slug ? getBlogBySlug(slug) : undefined;

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | AllTools Blog`;
      
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', post.description);
      
      // Update meta keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', post.keywords.join(', '));
      
      // Update canonical URL - IMPORTANT for SEO
      const canonicalUrl = `https://www.alltools.sbs${location.pathname}`;
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonicalLink) {
        canonicalLink.href = canonicalUrl;
      } else {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        canonicalLink.href = canonicalUrl;
        document.head.appendChild(canonicalLink);
      }
      
      // Update Open Graph URL
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
      
      // Update Open Graph title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', `${post.title} | AllTools`);
      
      // Update Open Graph description
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', post.description);
    }
    
    return () => {
      document.title = 'AllTools - Free Online Calculators, Image Tools & Converters';
      // Reset canonical to homepage
      const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonicalLink) canonicalLink.href = 'https://www.alltools.sbs';
    };
  }, [post, location.pathname]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getBlogsByTool(post.tool).filter(p => p.id !== post.id).slice(0, 3);

  // Simple markdown-like rendering
  const renderContent = (content: string) => {
    const lines = content.trim().split('\n');
    const elements: React.ReactNode[] = [];
    let inList = false;
    let listItems: string[] = [];
    let inTable = false;
    let tableRows: string[][] = [];
    let inCodeBlock = false;
    let codeLines: string[] = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc pl-6 mb-4 space-y-1">
            {listItems.map((item, i) => <li key={i} className="text-gray-700">{item}</li>)}
          </ul>
        );
        listItems = [];
      }
      inList = false;
    };

    const flushTable = () => {
      if (tableRows.length > 0) {
        const [header, ...body] = tableRows;
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto mb-4">
            <table className="min-w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  {header.map((cell, i) => (
                    <th key={i} className="px-4 py-2 text-left text-sm font-semibold text-gray-900 border-b">{cell}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.filter(row => !row[0].startsWith('--')).map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-4 py-2 text-sm text-gray-700 border-b">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
      }
      inTable = false;
    };

    const flushCode = () => {
      if (codeLines.length > 0) {
        elements.push(
          <pre key={`code-${elements.length}`} className="bg-gray-900 text-gray-100 p-4 rounded-lg mb-4 overflow-x-auto text-sm">
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        codeLines = [];
      }
      inCodeBlock = false;
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      
      // Code blocks
      if (trimmed.startsWith('```')) {
        if (inCodeBlock) {
          flushCode();
        } else {
          flushList();
          flushTable();
          inCodeBlock = true;
        }
        return;
      }
      
      if (inCodeBlock) {
        codeLines.push(line);
        return;
      }

      // Tables
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        flushList();
        inTable = true;
        const cells = trimmed.split('|').filter(c => c.trim()).map(c => c.trim());
        tableRows.push(cells);
        return;
      } else if (inTable) {
        flushTable();
      }

      // Headers
      if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={idx} className="text-2xl font-bold text-gray-900 mt-8 mb-4">{trimmed.slice(3)}</h2>);
        return;
      }
      if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={idx} className="text-xl font-semibold text-gray-900 mt-6 mb-3">{trimmed.slice(4)}</h3>);
        return;
      }

      // Lists
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        inList = true;
        listItems.push(trimmed.slice(2));
        return;
      }
      if (trimmed.match(/^\d+\. /)) {
        inList = true;
        listItems.push(trimmed.replace(/^\d+\. /, ''));
        return;
      }
      if (inList && trimmed === '') {
        flushList();
        return;
      }

      // Bold text
      let processed = trimmed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      
      // Links
      processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary-600 hover:underline">$1</a>');

      // Paragraphs
      if (trimmed && !trimmed.startsWith('|')) {
        flushList();
        elements.push(
          <p key={idx} className="text-gray-700 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: processed }} />
        );
      }
    });

    flushList();
    flushTable();
    flushCode();

    return elements;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Link to="/blog" className="inline-flex items-center text-gray-600 hover:text-primary-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Meta */}
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
            {post.category}
          </span>
          <span className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-1" />
            {new Date(post.publishedAt).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
        <p className="text-xl text-gray-600 mb-8">{post.description}</p>

        {/* Content */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-10">
          {renderContent(post.content)}
        </div>

        {/* Keywords */}
        <div className="mt-8 flex flex-wrap gap-2">
          {post.keywords.map(keyword => (
            <span key={keyword} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
              <Tag className="w-3 h-3 inline mr-1" />{keyword}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">Try Our {post.category}</h3>
          <p className="text-primary-100 mb-4">Use our free online tools to get instant results</p>
          <Link
            to={`/?tool=${post.tool}`}
            className="inline-flex items-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Open Tool <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {relatedPosts.map(related => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <h4 className="font-medium text-gray-900 mb-2 line-clamp-2">{related.title}</h4>
                  <p className="text-sm text-gray-500 line-clamp-2">{related.description}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPost;
